// src/components/circles/WelcomeToCircle.jsx
import { motion } from 'framer-motion';
import { Sparkles, Users, ArrowRight, Copy, Check } from 'lucide-react';
import { useState } from 'react';

export default function WelcomeToCircle({ circle, onContinue }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(circle.inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, type: 'spring' }}
        className="max-w-2xl w-full bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 rounded-3xl p-8 sm:p-12 shadow-2xl border border-purple-500/30"
      >
        {/* Celebration Header */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="text-center mb-8"
        >
          <div className="text-7xl mb-4" aria-hidden="true">🎉</div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3">
            Circle created
          </h1>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="text-5xl">{circle.emoji}</div>
            <h2 className="text-3xl font-bold text-purple-200">{circle.name}</h2>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-6"
        >
          {/* Invite Code Section */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-purple-400/30">
            <h3 className="text-xl font-bold text-white mb-3 flex items-center gap-2">
              <Users className="w-5 h-5 text-purple-300" />
              Invite your friends
            </h3>
            <p className="text-purple-200 mb-4">
              Send this code privately to the friends you want in the circle. Anyone with it can join, so keep
              it off anywhere public.
            </p>

            <div className="bg-slate-900/50 rounded-xl p-4 mb-4 border-2 border-purple-500/30">
              <div className="text-center">
                <p className="text-purple-300 text-sm mb-2">Your Invite Code</p>
                <p className="text-5xl font-bold text-white tracking-wider font-mono">
                  {circle.inviteCode}
                </p>
              </div>
            </div>

            <button
              onClick={handleCopy}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-5 h-5" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-5 h-5" />
                  Copy Invite Code
                </>
              )}
            </button>
          </div>

          {/* Next Steps */}
          <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-sm rounded-2xl p-6 border border-blue-400/20">
            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              What happens next
            </h3>
            <ul className="space-y-3 text-purple-100">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">1</span>
                <span>Send the code in a direct message, your own group chat, or in person</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">2</span>
                <span>Pick the module you&apos;re all on. Anyone in the circle can change it</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-sm font-bold">3</span>
                <span>Once a friend has joined, ask the circle a question: everyone picks an answer, and they all show at once</span>
              </li>
            </ul>
          </div>

          {/* CTA Button */}
          <button
            onClick={onContinue}
            className="w-full group bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-bold py-4 px-6 rounded-xl transition-all hover:scale-105 shadow-lg flex items-center justify-center gap-2"
          >
            <span>Go to your circle</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </button>

          <p className="text-center text-purple-300 text-sm">
            Your code is always on the circle&apos;s page, with a button to swap it for a new one
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
}
